import React, { useCallback, useContext, useEffect, useState } from "react";
import i18next from "i18next";
import { Box, Collapse, Popover, Tooltip, Typography } from "@mui/material";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import { makeStyles } from "tss-react/mui";
import { useHistory, useLocation } from "react-router-dom";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, hexToRgba, MEDIA } from "./lightTheme";
import { Context } from "states/Store";
import { getAccountThirdparties } from "services/edge/ThirdpartyService";
import { getAccountAttributesByBondType } from "services/edge/AttributesService";
import { hasAttributeListItems } from "services/common/AttributeUtils";
import { AttributeType } from "services/common/Constants";
import ROUTES, { getUrlWithAccountId } from "config/Routes";
import { formatCalculationResult } from "services/common/CalculatorUtils";
import { getAccountCalculations } from "services/edge/CalculatorService";
import LightLoader from "./LightLoader";

import { normColor } from "./utils/lightUtils";

/** Just the raw identifier number, no type label */
const getRawIdentifier = (tp) => tp.siret || tp.siren || tp.duns || tp.tva || tp.identifier || "";

// ─── Styles ────────────────────────────────────────────────────────────────────
const useStyles = makeStyles()(() => ({
  // ── Table ──
  tableWrap: {
    width: "100%", backgroundColor: BRAND.white, borderRadius: RADIUS.lg,
    border: `1.5px solid ${BRAND.border}`, boxShadow: SHADOW.soft,
  },
  tableHeader: {
    display: "grid", alignItems: "center",
    padding: "0.75rem 1.25rem", backgroundColor: BRAND.background,
    borderBottom: `1px solid ${BRAND.border}`, gap: "0.5rem",
    position: "sticky", top: "64px", zIndex: 10,
    [MEDIA.smDown]: { display: "none" },
  },
  tableHeaderCell: {
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family,
    lineHeight: 1.3, wordBreak: "break-word",
  },

  // ── Row ──
  tableRow: {
    display: "grid",
    padding: "1rem 1.25rem", alignItems: "center", gap: "0.5rem",
    cursor: "pointer", transition: "background 0.12s",
    borderBottom: `1px solid ${BRAND.border}`,
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: BRAND.background },
    "&:hover $rowAction": { opacity: 1 },
    [MEDIA.smDown]: {
      // Override the inline `gridTemplateColumns` via !important so the row
      // flows as a vertical stack on mobile.
      gridTemplateColumns: "1fr !important",
      gap: "0.5rem",
      padding: "0.85rem 1rem",
    },
  },
  rowAction: {
    display: "flex", alignItems: "center", justifyContent: "flex-end",
    gap: "0.2rem", color: BRAND.primary, fontFamily: FONT.family,
    fontSize: "0.72rem", fontWeight: 600, opacity: 0.4,
    transition: "opacity 0.15s",
    [MEDIA.smDown]: { opacity: 0.9, justifyContent: "flex-end" },
  },

  // ── TP cell ──
  tpCell: { display: "flex", alignItems: "flex-start", gap: "0.75rem", minWidth: 0 },
  tpIcon: {
    width: "36px", height: "36px", borderRadius: RADIUS.md, backgroundColor: BRAND.background,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "2px",
  },
  tpInfo: { display: "flex", flexDirection: "column", gap: "0.3rem", minWidth: 0 },
  tpName: {
    fontSize: "0.875rem", fontWeight: 600, color: BRAND.textMain,
    fontFamily: FONT.family, whiteSpace: "nowrap", overflow: "hidden",
    textOverflow: "ellipsis", lineHeight: 1.3,
  },
  tpCity: { fontSize: "0.72rem", color: BRAND.textMuted, fontFamily: FONT.family, lineHeight: 1.2 },
  tpIndicators: { display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.15rem" },
  indicatorTag: {
    display: "inline-flex", alignItems: "center", gap: "0.3rem", borderRadius: RADIUS.sm,
    padding: "2px 8px", fontSize: "0.65rem", fontWeight: 600,
    fontFamily: FONT.family, whiteSpace: "nowrap", lineHeight: 1.4,
  },
  indicatorDot: { width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0 },

  copyable: {
    display: "flex", alignItems: "center", gap: "0.3rem",
    minWidth: 0,
    "& svg": { opacity: 0, fontSize: "11px", color: BRAND.textMuted, transition: "opacity 0.15s", flexShrink: 0, cursor: "pointer" },
    "&:hover svg": { opacity: 0.6 },
  },
  cellId: {
    display: "flex", alignItems: "center", gap: "0.35rem",
    fontSize: "0.8rem", color: BRAND.textMuted, fontFamily: FONT.family,
    minWidth: 0,
    "& span": { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 },
    "& svg": { opacity: 0, fontSize: "12px", transition: "opacity 0.15s", flexShrink: 0, cursor: "pointer" },
    "&:hover svg": { opacity: 0.6 },
  },
  cellDate: { fontSize: "0.8rem", color: BRAND.textMuted, fontFamily: FONT.family },

  // ── Collapsible filters (mobile) ──
  filterToggle: {
    display: "none",
    [MEDIA.smDown]: {
      display: "flex", alignItems: "center", gap: "0.5rem",
      cursor: "pointer", width: "100%",
      marginBottom: "0.25rem",
      userSelect: "none",
    },
  },
  filterToggleLabel: {
    ...FONT.body, fontWeight: 700, color: BRAND.textMain, fontFamily: FONT.family,
    display: "flex", alignItems: "center", gap: "0.4rem", flex: 1,
  },
  filterCountBadge: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: "20px", height: "20px", padding: "0 6px",
    borderRadius: "10px", backgroundColor: BRAND.primary, color: BRAND.white,
    ...FONT.tiny, fontFamily: FONT.family,
  },
  filterCollapse: {
    width: "100%",
  },

  // Indicator cell — hidden on mobile to reduce density
  indicatorCell: {
    display: "flex", justifyContent: "center",
    [MEDIA.smDown]: { display: "none !important" },
  },
}));

// ─── Main component ────────────────────────────────────────────────────────────
export default function LightThirdparties() {
  const { classes: s, cx } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const location = useLocation();
  const initialArchived = location.state?.archived ?? location.state?.listArchived ?? false;
  const initialListFilters = location.state?.listFilters ?? null;
  const initialPage = location.state?.listPage ?? 0;
  const initialIndFilters = location.state?.listIndicatorFilters ?? {};
  const initialAttrFilters = location.state?.listAttributeFilters ?? {};

  const ROW_PER_PAGE = 20;

  const [thirdparties, setThirdparties] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(true);
  const [viewArchived, setViewArchived] = useState(initialArchived);
  // Filters collapsed by default on mobile, open on desktop
  const [filtersOpen, setFiltersOpen] = useState(() => typeof window === "undefined" || window.innerWidth >= 600);

  // Filters
  const EMPTY_FILTERS = { name: "", companyIdType: "CIT001", identifier: "" };
  const restoredFilters = initialListFilters ?? EMPTY_FILTERS;
  const [filters, setFilters] = useState(restoredFilters);
  const [pendingFilters, setPendingFilters] = useState(restoredFilters);

  const [indicatorFilters, setIndicatorFilters] = useState(initialIndFilters);

  const [attributeFilters, setAttributeFilters] = useState(initialAttrFilters);
  const indColsKey = `light_tp_indCols_${state.account?.id ?? ""}`;
  const [visibleIndKeys, setVisibleIndKeys] = useState(() => {
    try { const v = sessionStorage.getItem(indColsKey); return v ? JSON.parse(v) : null; } catch (_) { return null; }
  });
  const [indColAnchor, setIndColAnchor] = useState(null);

  const updateVisibleIndKeys = (keys) => {
    setVisibleIndKeys(keys);
    try { sessionStorage.setItem(indColsKey, JSON.stringify(keys)); } catch (_) {}
  };
  const [attrDefs, setAttrDefs] = useState([]);
  const hasRestoredExtraFilters = Object.keys(initialIndFilters).length > 0 || Object.keys(initialAttrFilters).length > 0;
  const [pendingAttrKey, setPendingAttrKey] = useState(hasRestoredExtraFilters ? "" : "__closed");
  const [pendingAttrValue, setPendingAttrValue] = useState("");

  // Collect unique indicator values from all loaded thirdparties
  const [indicatorOptions, setIndicatorOptions] = useState([]);

  useEffect(() => {
    if (!state.account?.id) return;
    getAccountCalculations(state.account.id, "THIRD_PARTY", dispatch)
      .then((res) => {
        const calcs = res?.content ?? (Array.isArray(res) ? res : []);
        const opts = calcs
          .filter((c) => c.branches?.length > 0)
          .map((c) => ({
            key: c.name,
            label: c.name,
            values: [...new Map(c.branches.map((b) => [b.resultValue, normColor(b.resultColorCode) || "#94A3B8"])).entries()]
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([v, color]) => ({ value: v, color })),
          }));
        setIndicatorOptions(opts);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id]);

  const loadThirdparties = useCallback(async (p = 0, activeFilters = filters, archived = viewArchived, indFilters = indicatorFilters, attrFilters = attributeFilters) => {
    if (!state.account?.id) return;
    setLoading(true);
    const filterLine = {};
    if (activeFilters.name) filterLine.name = activeFilters.name;
    if (activeFilters.identifier) {
      filterLine.identifier = activeFilters.identifier;
      filterLine.companyIdType = activeFilters.companyIdType;
    }
    // Indicator filters
    const activeIndFilters = Object.entries(indFilters).filter(([, v]) => v);
    if (activeIndFilters.length > 0) {
      filterLine.indicators = activeIndFilters.map(([id, values]) => ({ id, values }));
    }
    // Attribute filters
    const activeAttrFilters = Object.entries(attrFilters).filter(([, v]) => v);
    if (activeAttrFilters.length > 0) {
      filterLine.attributes = activeAttrFilters.map(([id, values]) => ({ id, values }));
    }
    try {
      const res = await getAccountThirdparties(
        state.account.id, !archived,
        { page: p, rowPerPage: ROW_PER_PAGE },
        filterLine,
        [{ field: archived ? "stoppingDate" : "followingDate", sort: "desc" }],
        dispatch,
      );
      setThirdparties(res?.content ?? []);
      setTotalElements(res?.totalElements ?? res?.globalCount ?? 0);
    } catch (_) {
      setThirdparties([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id, filters]);

  const handleApplyFilters = () => { setFilters(pendingFilters); setPage(0); loadThirdparties(0, pendingFilters, viewArchived, indicatorFilters, attributeFilters); };
  const handleResetFilters = () => { setPendingFilters(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setIndicatorFilters({}); setAttributeFilters({}); setPage(0); loadThirdparties(0, EMPTY_FILTERS, viewArchived, {}, {}); };
  const handlePageChange = (p) => { setPage(p); loadThirdparties(p, filters, viewArchived, indicatorFilters, attributeFilters); };
  const handleToggleArchived = (archived) => {
    setViewArchived(archived);
    setPage(0);
    setPendingFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setIndicatorFilters({});
    setAttributeFilters({});
    loadThirdparties(0, EMPTY_FILTERS, archived, {}, {});
  };
  const handleIndicatorFilter = (key, value) => {
    const next = { ...indicatorFilters, [key]: value || undefined };
    if (!value) delete next[key];
    setIndicatorFilters(next);
    setPage(0);
    loadThirdparties(0, filters, viewArchived, next, attributeFilters);
  };
  const handleAttributeFilter = (key, value) => {
    const next = { ...attributeFilters, [key]: value || undefined };
    if (!value) delete next[key];
    setAttributeFilters(next);
    setPage(0);
    loadThirdparties(0, filters, viewArchived, indicatorFilters, next);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadThirdparties(initialPage, restoredFilters, initialArchived, initialIndFilters, initialAttrFilters); }, [state.account?.id]);

  // Load attribute definitions (LIST types only, for filter dropdowns)
  useEffect(() => {
    if (!state.account?.id) return;
    getAccountAttributesByBondType(state.account.id, "thirdparty", null)
      .then((r) => {
        const defs = (r?.content ?? []).filter((d) =>
          hasAttributeListItems(d.type) || d.type === AttributeType.BOOLEAN
        );
        setAttrDefs(defs);
      })
      .catch(() => {});
  }, [state.account?.id]);

  const getTpUrl = (tp) => getUrlWithAccountId(state.account.id, `light/thirdparties/${tp.thirdpartyId}`);
  const handleClick = (e, tp) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) return; // let browser handle new tab
    e.preventDefault();
    history.push({
      pathname: getTpUrl(tp),
      state: { listFilters: filters, listPage: page, listArchived: viewArchived, listIndicatorFilters: indicatorFilters, listAttributeFilters: attributeFilters },
    });
  };

  return (
    <Box className={light.pageRoot}>
      {/* Banner header */}
      <Box className={light.pageHeader} style={viewArchived ? { backgroundColor: "#1D2939", backgroundImage: "none" } : undefined}>
        <Box className={light.pageHeaderInner} sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: { xs: "0.75rem", sm: 0 } }}>
          <Box>
            <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <Typography className={light.pageTitleWhite} sx={{ fontSize: { xs: "1.25rem !important", sm: undefined } }}>{i18next.t("light.thirdparties.title")}</Typography>
              {viewArchived && (
                <Box style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", backgroundColor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "20px", padding: "0.2rem 0.75rem", ...FONT.small, fontWeight: 700, color: "#FCA5A5", fontFamily: FONT.family }}>
                  Archives
                </Box>
              )}
            </Box>
            <Typography className={light.pageSubtitleWhite}>
              {loading ? "" : viewArchived ? i18next.t("light.thirdparties.countArchived", { count: totalElements }) : i18next.t("light.thirdparties.countActive", { count: totalElements })}
            </Typography>
          </Box>
          {!viewArchived && (
            <button
              className={light.btnPrimary}
              onClick={() => history.push(getUrlWithAccountId(state.account.id, ROUTES.LIGHT_LAUNCH))}
              style={{ display: "flex", alignItems: "center", gap: "0.4rem", backgroundColor: BRAND.secondary, color: BRAND.primary, whiteSpace: "nowrap", flexShrink: 0 }}
            >
              <AddCircleOutlineIcon style={{ fontSize: "16px" }} />
              {i18next.t("light.navbar.launch")}
            </button>
          )}
        </Box>
      </Box>

      <Box className={light.pageInner} style={{ marginTop: "-0.75rem" }}>
        {/* Toggle Actifs / Archivés */}
        <Box style={{ marginBottom: "1rem" }}>
          <Box className={light.toggleGroup}>
            <button className={cx(light.toggleBtn, !viewArchived && light.toggleBtnActive)} onClick={() => handleToggleArchived(false)}>{i18next.t("light.thirdparties.active")}</button>
            <button className={cx(light.toggleBtn, viewArchived && light.toggleBtnActive)} onClick={() => handleToggleArchived(true)}>{i18next.t("light.thirdparties.archived")}</button>
          </Box>
        </Box>

        {/* Filters */}
        <Box className={light.filterCard}>
          {/* Mobile-only toggle */}
          <Box className={s.filterToggle} onClick={() => setFiltersOpen((o) => !o)}>
            <Typography className={s.filterToggleLabel}>
              <TuneIcon style={{ fontSize: "17px", color: BRAND.primary }} />
              {i18next.t("light.common.filters") || "Filtres"}
              {(() => {
                const active =
                  (filters.name ? 1 : 0) +
                  (filters.identifier ? 1 : 0) +
                  Object.values(indicatorFilters).filter(Boolean).length +
                  Object.values(attributeFilters).filter(Boolean).length;
                return active > 0 ? <span className={s.filterCountBadge}>{active}</span> : null;
              })()}
            </Typography>
            <ExpandMoreIcon style={{ color: BRAND.textMuted, transform: filtersOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </Box>
          <Collapse in={filtersOpen} className={s.filterCollapse}>
          <Box className={light.filterRow}>
            <Box className={light.filterField}>
              <Typography className={light.fieldLabel}>{i18next.t("light.evaluations.supplier")}</Typography>
              <input
                className={light.fieldInput} placeholder={i18next.t("search") + "…"}
                value={pendingFilters.name}
                onChange={(e) => setPendingFilters((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
              />
            </Box>
            <Box className={light.filterField} style={{ flex: "1 1 280px" }}>
              <Typography className={light.fieldLabel}>{i18next.t("identifier")}</Typography>
              <Box style={{ display: "flex", gap: "0.4rem" }}>
                <select
                  className={light.fieldSelect} style={{ flex: "0 0 140px" }}
                  value={pendingFilters.companyIdType}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, companyIdType: e.target.value }))}
                >
                  <option value="CIT003">Duns</option>
                  <option value="CIT001">{i18next.t("identifier")}</option>
                  <option value="CIT002">TVA</option>
                </select>
                <input
                  className={light.fieldInput} placeholder="Valeur…"
                  value={pendingFilters.identifier}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, identifier: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                />
              </Box>
            </Box>
            {/* Attributs / Indicateurs inline */}
            {(indicatorOptions.length > 0 || attrDefs.length > 0) && (() => {
              const filterDefs = [
                ...indicatorOptions.map((o) => ({ key: `ind:${o.key}`, label: o.label, type: "indicator", values: o.values })),
                ...attrDefs.map((d) => ({ key: `attr:${d.label}`, label: d.label, type: d.type === AttributeType.BOOLEAN ? "boolean" : hasAttributeListItems(d.type) ? "list" : "text", values: d.type === AttributeType.BOOLEAN ? [{ value: "true", color: null }, { value: "false", color: null }] : (d.values ?? []).map((li) => ({ value: li.value, color: null })) })),
              ];
              const usedKeys = new Set([
                ...Object.keys(indicatorFilters).map((k) => `ind:${k}`),
                ...Object.keys(attributeFilters).map((k) => `attr:${k}`),
              ]);
              const selectedDef = filterDefs.find((d) => d.key === pendingAttrKey);
              return (
                <Box className={light.filterField} style={{ flex: "1 1 280px" }}>
                  <Typography className={light.fieldLabel}>{i18next.t("light.filters.attrsIndicators")}</Typography>
                  <Box style={{ display: "flex", gap: "0.4rem" }}>
                    <select
                      className={light.fieldSelect} style={{ flex: selectedDef ? "0 0 160px" : "1 1 auto" }}
                      value={pendingAttrKey === "__closed" ? "" : pendingAttrKey}
                      onChange={(e) => { setPendingAttrKey(e.target.value); setPendingAttrValue(""); }}
                    >
                      <option value="">{i18next.t("light.filters.chooseFilter")}</option>
                      {indicatorOptions.length > 0 && <optgroup label={i18next.t("light.filters.indicators")}>
                        {indicatorOptions.filter((o) => !usedKeys.has(`ind:${o.key}`)).map((o) => (
                          <option key={`ind:${o.key}`} value={`ind:${o.key}`}>{o.label}</option>
                        ))}
                      </optgroup>}
                      {attrDefs.length > 0 && <optgroup label={i18next.t("light.filters.attributes")}>
                        {attrDefs.filter((d) => !usedKeys.has(`attr:${d.label}`)).map((d) => (
                          <option key={`attr:${d.label}`} value={`attr:${d.label}`}>{d.label}</option>
                        ))}
                      </optgroup>}
                    </select>
                    {selectedDef && (
                      selectedDef.type === "text" ? (
                        <input
                          className={light.fieldInput} style={{ flex: 1 }}
                          placeholder={i18next.t("light.common.value") + "…"}
                          value={pendingAttrValue}
                          onChange={(e) => setPendingAttrValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && pendingAttrValue) { const isInd = pendingAttrKey.startsWith("ind:"); const realKey = pendingAttrKey.replace(/^(ind:|attr:)/, ""); isInd ? handleIndicatorFilter(realKey, pendingAttrValue) : handleAttributeFilter(realKey, pendingAttrValue); setPendingAttrKey(""); setPendingAttrValue(""); } }}
                        />
                      ) : (
                        <select
                          className={light.fieldSelect} style={{ flex: 1 }}
                          value={pendingAttrValue}
                          onChange={(e) => {
                            setPendingAttrValue(e.target.value);
                            if (e.target.value) {
                              const isInd = pendingAttrKey.startsWith("ind:");
                              const realKey = pendingAttrKey.replace(/^(ind:|attr:)/, "");
                              isInd ? handleIndicatorFilter(realKey, e.target.value) : handleAttributeFilter(realKey, e.target.value);
                              setPendingAttrKey(""); setPendingAttrValue("");
                            }
                          }}
                        >
                          <option value="">{i18next.t("light.common.choose")}</option>
                          {selectedDef.values.map((v) => (<option key={v.value} value={v.value}>{selectedDef.type === "boolean" ? (v.value === "true" ? i18next.t("yes") : i18next.t("no")) : v.value}</option>))}
                        </select>
                      )
                    )}
                  </Box>
                </Box>
              );
            })()}

            <Box className={light.filterActions}>
              <button className={cx(light.btnSecondary, light.btnSmall)} onClick={handleResetFilters}>{i18next.t("light.common.reset")}</button>
              <button className={cx(light.btnPrimary, light.btnSmall)} onClick={handleApplyFilters}>{i18next.t("search")}</button>
            </Box>
          </Box>
          </Collapse>
          {(Object.keys(indicatorFilters).length > 0 || Object.keys(attributeFilters).length > 0) && (
            <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", padding: "0.65rem 0 0" }}>
              {[
                ...Object.entries(indicatorFilters).filter(([, v]) => v).map(([k, v]) => ({ key: `ind:${k}`, label: k, value: v, type: "indicator" })),
                ...Object.entries(attributeFilters).filter(([, v]) => v).map(([k, v]) => ({ key: `attr:${k}`, label: k, value: v, type: "attribute" })),
              ].map((f) => {
                const color = f.type === "indicator" ? (indicatorOptions.find((o) => o.key === f.label)?.values?.find((v) => v.value === f.value)?.color) : null;
                return (
                  <Box key={f.key} className={light.attrTag} style={{ cursor: "pointer", gap: "0.3rem" }} onClick={() => f.type === "indicator" ? handleIndicatorFilter(f.label, "") : handleAttributeFilter(f.label, "")}>
                    <span className={light.attrTagLabel}>{f.label}</span>
                    <span className={light.attrTagValue} style={{ color: color || undefined }}>{(f.type === "attribute" && (f.value === "true" ? i18next.t("yes") : f.value === "false" ? i18next.t("no") : f.value)) || f.value}</span>
                    <span style={{ color: BRAND.textMuted, fontSize: "12px", lineHeight: 1 }}>×</span>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Table */}
        {loading && thirdparties.length === 0 ? (
          <LightLoader label={i18next.t("light.thirdparties.loading")} />
        ) : !loading && thirdparties.length === 0 ? (
          <Box className={cx(light.card, light.emptySection)}>
            <BusinessOutlinedIcon style={{ fontSize: "40px", color: BRAND.border, marginBottom: "0.75rem" }} />
            <Typography style={{ fontWeight: 600, color: BRAND.textMain, fontFamily: "Inter", marginBottom: "0.25rem" }}>
              {viewArchived ? i18next.t("light.thirdparties.noArchived") : i18next.t("light.thirdparties.noActive")}
            </Typography>
            {!viewArchived && (
              <Typography style={{ fontSize: "0.875rem", fontFamily: "Inter" }}>
                {i18next.t("light.thirdparties.addFirst")}
              </Typography>
            )}
          </Box>
        ) : (
          (() => {
            // Collect unique indicator columns from all loaded thirdparties
            const indicatorColumns = [];
            const seen = new Set();
            thirdparties.forEach((tp) => {
              (tp.indicators ?? []).forEach((ind) => {
                const key = ind.name || ind.label;
                if (key && !seen.has(key)) {
                  seen.add(key);
                  indicatorColumns.push({ key, label: ind.label || ind.name });
                }
              });
            });
            const MAX_IND = 6;
            const activeIndKeys = visibleIndKeys ?? indicatorColumns.slice(0, MAX_IND).map((c) => c.key);
            const visibleIndicators = indicatorColumns.filter((c) => activeIndKeys.includes(c.key));
            const hasIndicators = visibleIndicators.length > 0;
            const showColSelector = indicatorColumns.length > MAX_IND;
            const gridCols = hasIndicators
              ? `minmax(150px, 240px) 160px ${visibleIndicators.map(() => "1fr").join(" ")} 50px`
              : `1fr 200px 50px`;

            return (
              <>
              <Box className={s.tableWrap}>
                {/* Header */}
                <Box className={s.tableHeader} style={{ gridTemplateColumns: gridCols }}>
                  <Typography className={s.tableHeaderCell}>{i18next.t("light.evaluations.supplier")}</Typography>
                  <Typography className={s.tableHeaderCell}>{i18next.t("identifier")}</Typography>
                  {visibleIndicators.map((col) => (
                    <Tooltip key={col.key} title={col.label} placement="top" enterDelay={300} disableInteractive>
                      <Typography className={s.tableHeaderCell} style={{ textAlign: "center", ...(indicatorFilters[col.key] ? { color: BRAND.primary } : {}) }}>{col.label}</Typography>
                    </Tooltip>
                  ))}
                  {showColSelector ? (
                    <Tooltip title={i18next.t("light.common.columns")} placement="top" disableInteractive>
                      <Box
                        onClick={(e) => setIndColAnchor(e.currentTarget)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: BRAND.textMuted, transition: "color 0.15s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                      >
                        <ViewColumnOutlinedIcon style={{ fontSize: "16px" }} />
                      </Box>
                    </Tooltip>
                  ) : <Box />}
                </Box>

                {/* Rows */}
                {loading
                  ? <LightLoader size="sm" label={i18next.t("light.common.loading")} />
                  : thirdparties.map((tp) => {
                    const indicatorsMap = {};
                    (tp.indicators ?? []).forEach((ind) => {
                      const key = ind.name || ind.label;
                      const val = formatCalculationResult(ind);
                      const dv = val === true ? i18next.t("yes") : val === false ? i18next.t("no") : val;
                      if (key && (dv || dv === 0)) indicatorsMap[key] = { value: dv, color: normColor(ind.resultColorCode) || "#94A3B8" };
                    });

                    return (
                      <Box key={tp.thirdpartyId} component="a" href={getTpUrl(tp)} className={s.tableRow} style={{ gridTemplateColumns: gridCols, textDecoration: "none", color: "inherit" }} onClick={(e) => handleClick(e, tp)}>
                        {/* Fournisseur */}
                        <Box className={s.tpCell}>
                          <Box className={s.tpIcon}>
                            <BusinessOutlinedIcon style={{ fontSize: "18px", color: BRAND.primary }} />
                          </Box>
                          <Box className={s.tpInfo}>
                            <Tooltip title={tp.name} placement="top" enterDelay={400} disableInteractive>
                              <Box className={s.copyable}>
                                <Typography className={s.tpName}>{tp.name}</Typography>
                                <ContentCopyIcon onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(tp.name); }} />
                              </Box>
                            </Tooltip>
                            {tp.addressCity && <Typography className={s.tpCity}>{tp.addressCity}</Typography>}
                          </Box>
                        </Box>

                        {/* Identifiant */}
                        <Box className={s.cellId}>
                          <span>{getRawIdentifier(tp) || "—"}</span>
                          {getRawIdentifier(tp) && (
                            <Tooltip title="Copier" placement="top" disableInteractive>
                              <ContentCopyIcon onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(getRawIdentifier(tp)); }} />
                            </Tooltip>
                          )}
                        </Box>

                        {/* Indicator columns (max 6) — hidden on mobile */}
                        {visibleIndicators.map((col) => {
                          const ind = indicatorsMap[col.key];
                          if (!ind) return <Box key={col.key} className={s.indicatorCell} style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, textAlign: "center" }}>—</Box>;
                          return (
                            <Box key={col.key} className={s.indicatorCell}>
                              <Tooltip title={`${col.label} : ${ind.value}`} placement="top" enterDelay={300} disableInteractive>
                                <Box className={light.statusChip} style={{ backgroundColor: hexToRgba(ind.color, 0.22), color: BRAND.textMain }}>
                                  {ind.value}
                                </Box>
                              </Tooltip>
                            </Box>
                          );
                        })}

                        {/* Action */}
                        <Box className={s.rowAction}>
                          {i18next.t("light.common.see")} <ChevronRightIcon style={{ fontSize: "16px" }} />
                        </Box>
                      </Box>
                    );
                  })
                }

            {/* Pagination */}
            {totalElements > ROW_PER_PAGE && (
              <Box className={light.pagination}>
                <Typography className={light.paginationInfo}>
                  {page * ROW_PER_PAGE + 1}–{Math.min((page + 1) * ROW_PER_PAGE, totalElements)} sur {totalElements}
                </Typography>
                <Box className={light.paginationControls}>
                  <button className={light.pageBtn} disabled={page === 0} onClick={() => handlePageChange(page - 1)}>←</button>
                  {Array.from({ length: Math.ceil(totalElements / ROW_PER_PAGE) }, (_, i) => i)
                    .filter(i => Math.abs(i - page) <= 2)
                    .map(i => (
                      <button key={i} className={cx(light.pageBtn, i === page && light.pageBtnActive)} onClick={() => handlePageChange(i)}>
                        {i + 1}
                      </button>
                    ))
                  }
                  <button className={light.pageBtn} disabled={page >= Math.ceil(totalElements / ROW_PER_PAGE) - 1} onClick={() => handlePageChange(page + 1)}>→</button>
                </Box>
              </Box>
            )}
              </Box>

              {/* Column selector popover */}
              <Popover
                open={Boolean(indColAnchor)}
                anchorEl={indColAnchor}
                onClose={() => setIndColAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: { borderRadius: RADIUS.md, border: `1.5px solid ${BRAND.border}`, boxShadow: SHADOW.soft, p: 1.5, minWidth: 200 } }}
              >
                <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.5rem" }}>
                  {i18next.t("light.common.columns")}
                </Typography>
                {indicatorColumns.map((col) => {
                  const checked = activeIndKeys.includes(col.key);
                  return (
                    <label key={col.key} style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.25rem 0", cursor: "pointer", ...FONT.small, fontFamily: FONT.family, color: BRAND.textMain }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked ? activeIndKeys.filter((k) => k !== col.key) : [...activeIndKeys, col.key];
                          updateVisibleIndKeys(next);
                        }}
                        style={{ accentColor: BRAND.primary }}
                      />
                      {col.label}
                    </label>
                  );
                })}
              </Popover>
              </>
            );
          })()
        )}
      </Box>

    </Box>
  );
}
